<template>
    <section
        id="faq"
        aria-labelledby="faq-heading"
        class="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8"
    >
        <div class="mx-auto max-w-2xl text-center">
            <h2
                id="faq-heading"
                class="text-3xl font-bold tracking-tight"
            >
                Frequently asked questions
            </h2>
            <p class="mt-3 text-slate-600">
                If you still have questions, check our support and resources
                pages.
            </p>
        </div>

        <div class="mx-auto mt-10 grid max-w-4xl gap-4">
            <article
                v-for="faq in frequentlyAskedQuestions"
                :key="faq.id"
                class="rounded-xl border border-slate-200 bg-white p-6 shadow-xs shadow-slate-200/70"
                :aria-labelledby="`faq-question-${faq.id}`"
            >
                <h3
                    :id="`faq-question-${faq.id}`"
                    class="text-base font-bold text-slate-900"
                >
                    {{ faq.question }}
                </h3>
                <p class="mt-3 text-slate-600">
                    {{ faq.answer }}
                </p>
            </article>
        </div>
    </section>
</template>

<script setup lang="ts">
    interface FrequentlyAskedQuestion {
        id: number
        question: string
        answer: string
    }

    const frequentlyAskedQuestions: FrequentlyAskedQuestion[] = [
        {
            id: 1,
            question: 'How do clients book a service?',
            answer: 'Clients open your booking page, choose a service, pick a time, and fill out your intake form. TinyCal confirms the booking and syncs it with your calendar.',
        },
        {
            id: 2,
            question: 'Can I stop double bookings?',
            answer: 'Yes. TinyCal syncs with your calendar and applies availability rules so overlapping sessions are blocked automatically.',
        },
        {
            id: 3,
            question: 'Can clients pay when booking?',
            answer: 'Yes. You can accept payments through Stripe before a session is confirmed. Standard payment provider fees apply.',
        },
        {
            id: 4,
            question:
                'Does TinyCal work with Google Calendar, Outlook Calendar, Zoom, and Google Meet?',
            answer: 'Yes. Connect Google Calendar or Outlook Calendar to keep availability synced, and connect Zoom or Google Meet to automatically create meeting links when a session is booked.',
        },
        {
            id: 5,
            question: 'Can I run group sessions and workshops?',
            answer: 'Yes. TinyCal supports group scheduling where you can set attendee limits and optionally charge per attendee.',
        },
        {
            id: 6,
            question:
                'Can attendees cancel without canceling the whole meeting?',
            answer: 'Yes. In group meetings, attendees can cancel their own spot without canceling the session for everyone else.',
        },
        {
            id: 7,
            question: 'Do I need a full website first?',
            answer: 'No. TinyCal is built to help you start with one clean booking page so you can look professional and get booked without a big website project.',
        },
        {
            id: 8,
            question: 'What if I need help or have a feature request?',
            answer: 'You can use our blog, changelog, and feature request board, or contact us through support resources.',
        },
    ]
</script>
